import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateUtils } from 'react-day-picker'

import { SelectItemType } from 'Components/common/SelectPickerProps'
import { DateRange, DateRangeString, DayPickerRange, UserSettingsDynamic } from 'Models'
import { Formatter } from 'Utils'

export type DateRangePanelSettingsKey =
  | 'analyticsNutritionsDateRange'
  | 'analyticsEnergyHistoryDateRange'
  | 'analyticsEnergyDistributionDateRange'

type CalculateRangeFn = (dateRange: DateRangeString, appliedRange: DayPickerRange) => DateRange | null

type UseDateRangePanelParams = {
  analyticsDateRange?: DateRangeString
  settingsKey: DateRangePanelSettingsKey
  calculateRange: CalculateRangeFn
  fetchFn: (range: DateRange) => void
  refreshKey?: number
  updateUserSettings: (settings: UserSettingsDynamic) => void
}

export type UseDateRangePanelReturn = {
  range: DateRange | null | undefined
  customLabel: string | undefined
  showPicker: boolean
  setShowPicker: (show: boolean) => void
  handleApply: (range: DayPickerRange) => void
  handleChange: (item: SelectItemType) => void
  handleItemClick: (item: SelectItemType) => void
  labelOverrides: Record<string, string> | undefined
}

const DATE_FORMAT = 'DD.MM.YY'

const useDateRangePanel = ({
  analyticsDateRange,
  settingsKey,
  calculateRange,
  fetchFn,
  refreshKey,
  updateUserSettings
}: UseDateRangePanelParams): UseDateRangePanelReturn => {
  const [showPicker, setShowPicker] = useState(false)
  const [customRange, setCustomRange] = useState<DayPickerRange>({})

  const formatDate = (date: Date): string => Formatter.formatDate(date, DATE_FORMAT, DATE_FORMAT)

  const customLabel = useMemo(() => {
    const { from, to } = customRange

    if (!from || !to) {
      return undefined
    }

    if (DateUtils.isSameDay(from, to)) {
      return formatDate(from)
    }

    return `${formatDate(from)} – ${formatDate(to)}`
  }, [customRange])

  const range = useMemo(() => analyticsDateRange && calculateRange(analyticsDateRange, customRange), [
    analyticsDateRange,
    customRange,
    calculateRange
  ])

  const dateFrom = range?.dateFrom.getTime()
  const dateTo = range?.dateTo.getTime()

  useEffect(() => {
    if (dateFrom === undefined || dateTo === undefined) {
      return
    }

    fetchFn({ dateFrom: new Date(dateFrom), dateTo: new Date(dateTo) })
  }, [fetchFn, dateFrom, dateTo, refreshKey])

  const handleApply = useCallback(
    (range: DayPickerRange) => {
      updateUserSettings({ [settingsKey]: 'CUSTOM' } as UserSettingsDynamic)
      setCustomRange(range)
      setShowPicker(false)
    },
    [updateUserSettings, settingsKey]
  )

  const handleChange = useCallback(
    (item: SelectItemType) => {
      updateUserSettings({ [settingsKey]: item as DateRangeString } as UserSettingsDynamic)
    },
    [updateUserSettings, settingsKey]
  )

  const handleItemClick = useCallback(
    (item: SelectItemType) => {
      if (item === 'CUSTOM') {
        setShowPicker(true)

        return
      }

      if (analyticsDateRange === 'CUSTOM') {
        setCustomRange({})
      }
    },
    [analyticsDateRange, setShowPicker, setCustomRange]
  )

  return {
    range,
    customLabel,
    showPicker,
    setShowPicker,
    handleApply,
    handleChange,
    handleItemClick,
    labelOverrides: customLabel ? { CUSTOM: customLabel } : undefined
  }
}

export default useDateRangePanel
